import ChevronDownIcon from "@/components/_icons/chevronDownIcon";
import { DayPart } from "@/dto/dayPartMenu";
import { ScheduleDateType, ScheduleTimeType } from "@/dto/scheduledTime";
import { getSchedulableDateOptions } from "@/utils/scheduleOrderUtils";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { DateTime } from "luxon";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

export const ScheduledDateDropdown = ({
  scheduleDate,
  setScheduleDate,
  scheduleDateRange,
  setScheduleTime,
  businessHours,
  timezone,
  leadTime,
  selectedDayPart = null,
}: {
  scheduleDate: ScheduleDateType;
  // eslint-disable-next-line no-unused-vars
  setScheduleDate: (newDate: ScheduleDateType) => void;
  scheduleDateRange: number;
  // eslint-disable-next-line no-unused-vars
  setScheduleTime: (newTime: ScheduleTimeType) => void;
  businessHours: any;
  timezone: string;
  leadTime?: number;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("ScheduledDateDropdown");
  const tPickup = useTranslations("CalculatePickTime");
  const dateOptions: ScheduleDateType[] = getSchedulableDateOptions(
    scheduleDateRange,
    businessHours,
    timezone,
    tPickup,
    leadTime,
    selectedDayPart
  );

  useEffect(() => {
    if (
      !dateOptions.some((option) => {
        const scheduleDateObj = DateTime.fromJSDate(
          typeof scheduleDate.dateObject === "string"
            ? new Date(scheduleDate.dateObject)
            : scheduleDate.dateObject,
          {
            zone: timezone,
          }
        );
        const optionDateObj = DateTime.fromJSDate(option.dateObject, {
          zone: timezone,
        });

        return (
          option.displayValue === scheduleDate.displayValue &&
          optionDateObj.hasSame(scheduleDateObj, "day")
        );
      })
    ) {
      setScheduleDate(dateOptions[0]);
    }
  }, [dateOptions, scheduleDate, setScheduleDate, timezone]);

  const isDisabled = dateOptions.length === 0;

  return (
    <Listbox
      value={scheduleDate}
      onChange={(option) => {
        setScheduleDate(option);
        setScheduleTime({
          displayValue: "",
          timeObject: option.dateObject,
        });
      }}
    >
      {({ open }) => (
        <div className="relative">
          <ListboxButton
            className={
              "flex items-center gap-x-2 w-full leading-6 text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-white"
            }
            aria-label={t("date")}
            disabled={isDisabled}
          >
            <input
              type="text"
              id="scheduled_date_input"
              className="cursor-pointer peer text-[15px] leading-6 block w-full h-[52px] rounded-[4px] border enabled:border-black bg-transparent px-2 pt-6 pb-2 pr-10 text-gray-900 placeholder-transparent hover:border-black disabled:border-[#C7C7C7] disabled:text-[#C7C7C7] disabled:cursor-not-allowed tracking-wide"
              value={scheduleDate?.displayValue}
              disabled={isDisabled}
              readOnly
            />
            <label
              htmlFor="scheduled_date_input"
              className={`text-xs leading-6 absolute py-0.5 left-2 top-3 origin-[0] -translate-y-2.5 tracking-wide ${
                isDisabled ? "text-[#C7C7C7]" : "text-[#727272]"
              }`}
            >
              {t("date")}
            </label>
            {dateOptions.length > 1 && (
              <ChevronDownIcon
                aria-hidden="true"
                className={`cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-transform duration-300 ${
                  open ? "rotate-180" : "rotate-0"
                }`}
              />
            )}
          </ListboxButton>
          <ListboxOptions
            transition
            className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            <div className="p-3">
              {dateOptions.map((option) => (
                <ListboxOption
                  key={option.displayValue}
                  value={option}
                  className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50 flex-auto"
                  aria-label={option.displayValue}
                >
                  <p className="text-[15px]">{option.displayValue}</p>
                </ListboxOption>
              ))}
            </div>
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  );
};
